import { z } from 'zod';
import {
  AD_PLACEMENTS,
  CONSENT_NOTICE_DIGESTS,
  PRODUCT,
  SESSION_DURATIONS,
  SESSION_INTENTS,
  SESSION_MODALITIES,
  SUPPORTED_LANGUAGES,
  TOPIC_CATEGORIES,
} from './constants';

const languageCodes = SUPPORTED_LANGUAGES.map((language) => language.code) as [string, ...string[]];
const topicIds = TOPIC_CATEGORIES.map((topic) => topic.id) as [string, ...string[]];

export const countryCodeSchema = z.string().regex(/^[A-Z]{2}$/);
export const languageSchema = z.enum(languageCodes);
export const topicCategorySchema = z.enum(topicIds);

export const onboardingSchema = z.object({
  installationId: z.uuid(),
  alias: z.string().trim().min(2).max(40),
  locale: z.string().min(2).max(35),
  timezone: z.string().min(1).max(64),
  declaredCountry: countryCodeSchema,
  ageConfirmed: z.literal(true),
  accepted: z.literal(true),
  consentLanguage: z.enum(['de', 'en']),
  consentPolicyVersion: z.literal(PRODUCT.consentPolicyVersion),
  communityPolicyVersion: z.literal(PRODUCT.communityPolicyVersion),
  noticeDigest: z.enum([CONSENT_NOTICE_DIGESTS.de, CONSENT_NOTICE_DIGESTS.en]),
}).superRefine((value, context) => {
  if (value.noticeDigest !== CONSENT_NOTICE_DIGESTS[value.consentLanguage]) {
    context.addIssue({
      code: 'custom',
      path: ['noticeDigest'],
      message: 'noticeDigest must match consentLanguage',
    });
  }
});

export const authResponseSchema = z.object({
  accessToken: z.string().min(24),
  account: z.object({
    id: z.string(),
    alias: z.string(),
    locale: z.string(),
    timezone: z.string(),
    declaredCountry: countryCodeSchema,
  }),
});

export const availabilitySchema = z.object({
  startAt: z.iso.datetime(),
  endAt: z.iso.datetime(),
}).refine((value) => Date.parse(value.endAt) > Date.parse(value.startAt), {
  message: 'endAt must be after startAt',
});

export const conversationRequestSchema = z.object({
  categoryId: topicCategorySchema,
  topic: z.string().trim().min(3).max(180),
  intent: z.enum(SESSION_INTENTS),
  languages: z.array(languageSchema).min(1).max(5),
  preferredLanguage: languageSchema,
  coordinationLanguage: languageSchema,
  availability: z.array(availabilitySchema).min(1).max(6),
  durationMinutes: z.union(SESSION_DURATIONS.map((duration) => z.literal(duration)) as [z.ZodLiteral<number>, z.ZodLiteral<number>, ...z.ZodLiteral<number>[]]),
  modality: z.enum(SESSION_MODALITIES),
}).refine((value) => value.languages.includes(value.preferredLanguage), {
  message: 'preferredLanguage must be selected',
  path: ['preferredLanguage'],
}).refine((value) => value.languages.includes(value.coordinationLanguage), {
  message: 'coordinationLanguage must be selected',
  path: ['coordinationLanguage'],
});

export const matchDecisionSchema = z.object({
  decision: z.enum(['accepted', 'declined']),
});

export const sessionFeedbackSchema = z.object({
  feedbackToken: z.string().min(24),
  completed: z.boolean(),
  topicFit: z.number().int().min(1).max(5),
  conversationValue: z.number().int().min(1).max(5),
  feltSafe: z.boolean(),
  adRelevance: z.number().int().min(1).max(5),
  adWasClear: z.boolean(),
});

export const sessionContextSchema = z.object({
  contextToken: z.string().min(24),
  sequence: z.number().int().nonnegative(),
  language: languageSchema,
  textFragment: z.string().trim().min(2).max(500),
}).strict();

export const adDecisionRequestSchema = z.object({
  placement: z.enum(AD_PLACEMENTS),
  language: languageSchema,
});

export const campaignSchema = z.object({
  name: z.string().trim().min(2).max(100),
  sponsorName: z.string().trim().min(2).max(100),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  budgetCents: z.number().int().min(100),
  countryCodes: z.array(countryCodeSchema).min(1).max(50),
  languages: z.array(languageSchema).min(1).max(15),
  categoryIds: z.array(topicCategorySchema).min(1).max(10),
  contextTags: z.array(z.string().regex(/^[a-z][a-z0-9_-]*(\.[a-z0-9_-]+)+$/)).max(30).default([]),
  placements: z.array(z.enum(AD_PLACEMENTS)).min(1),
  headline: z.string().trim().min(2).max(90),
  body: z.string().trim().min(2).max(220),
  ctaLabel: z.string().trim().min(2).max(35),
  destinationUrl: z.url().refine((url) => url.startsWith('https://'), 'HTTPS required'),
  creativeAssetUrl: z.url().optional(),
});

export type OnboardingInput = z.infer<typeof onboardingSchema>;
export type AuthResponse = z.infer<typeof authResponseSchema>;
export type ConversationRequestInput = z.infer<typeof conversationRequestSchema>;
export type SessionFeedbackInput = z.infer<typeof sessionFeedbackSchema>;
export type SessionContextInput = z.infer<typeof sessionContextSchema>;
export type CampaignInput = z.infer<typeof campaignSchema>;
